import { GRAMS_PER_OZ, GRAMS_PER_TOLA, OZ_PER_TOLA_FORMULA } from "./gold-data";

export type SilverCountry = "pakistan" | "india";

// Pure arithmetic silver model — fixed local-market premiums per regional matrix.
// Fixed local-market premium (Pakistan). Calibrated so that
// (spotUsdPerOz / 2.67) * pkrPerUsd + premium ≈ live Karachi/Lahore retail
// per-tola rate. Bump this value whenever the live page drifts >Rs 200
// from real bullion market prices.
export const SILVER_PREMIUM = 900;          // Rs per tola (Pakistan)
export const SILVER_PREMIUM_PK = 900;       // Rs per tola (Pakistan)
export const SILVER_PREMIUM_IN = 75;        // Rs per 10g (India)
export const SILVER_DIVISOR = GRAMS_PER_OZ / GRAMS_PER_TOLA;

// India: 22% multiplier on top of (spot×FX + flat premium). Silver retail
// gap in India is typically wider than gold (duty + GST + dealer margin),
// so a larger multiplicative premium matches observed bullion prices.
export const INDIA_SILVER_DUTY_MULTIPLIER = 1.22;

export type SilverCityInfo = { slug: string; name: string; country: SilverCountry; aliasSlug: string };

export const SILVER_CITIES: SilverCityInfo[] = [
  { slug: "lahore",   name: "Lahore",   country: "pakistan", aliasSlug: "silver-rate-lahore" },
  { slug: "karachi",  name: "Karachi",  country: "pakistan", aliasSlug: "silver-rate-karachi" },
  { slug: "islamabad", name: "Islamabad", country: "pakistan", aliasSlug: "silver-rate-islamabad" },
  { slug: "mumbai",   name: "Mumbai",   country: "india",    aliasSlug: "silver-rate-mumbai" },
  { slug: "delhi",    name: "Delhi",    country: "india",    aliasSlug: "chandi-ka-rate-delhi" },
  { slug: "chennai",  name: "Chennai",  country: "india",    aliasSlug: "silver-rate-chennai" },
  { slug: "rawalpindi", name: "Rawalpindi", country: "pakistan", aliasSlug: "silver-rate-rawalpindi" },
  { slug: "faisalabad", name: "Faisalabad", country: "pakistan", aliasSlug: "silver-rate-faisalabad" },
  { slug: "multan", name: "Multan", country: "pakistan", aliasSlug: "silver-rate-multan" },
  { slug: "peshawar", name: "Peshawar", country: "pakistan", aliasSlug: "silver-rate-peshawar" },
  { slug: "quetta", name: "Quetta", country: "pakistan", aliasSlug: "silver-rate-quetta" },
  { slug: "gujranwala", name: "Gujranwala", country: "pakistan", aliasSlug: "silver-rate-gujranwala" },
  { slug: "sialkot", name: "Sialkot", country: "pakistan", aliasSlug: "silver-rate-sialkot" },
  { slug: "hyderabad-pk", name: "Hyderabad, Pakistan", country: "pakistan", aliasSlug: "silver-rate-hyderabad-pakistan" },
  { slug: "bahawalpur", name: "Bahawalpur", country: "pakistan", aliasSlug: "silver-rate-bahawalpur" },
  { slug: "sargodha", name: "Sargodha", country: "pakistan", aliasSlug: "silver-rate-sargodha" },
  { slug: "bangalore", name: "Bangalore", country: "india", aliasSlug: "silver-rate-bangalore" },
  { slug: "hyderabad-in", name: "Hyderabad, India", country: "india", aliasSlug: "silver-rate-hyderabad-india" },
  { slug: "kolkata", name: "Kolkata", country: "india", aliasSlug: "silver-rate-kolkata" },
  { slug: "ahmedabad", name: "Ahmedabad", country: "india", aliasSlug: "silver-rate-ahmedabad" },
  { slug: "pune", name: "Pune", country: "india", aliasSlug: "silver-rate-pune" },
  { slug: "jaipur", name: "Jaipur", country: "india", aliasSlug: "chandi-ka-rate-jaipur" },
  { slug: "lucknow", name: "Lucknow", country: "india", aliasSlug: "chandi-ka-rate-lucknow" },
  { slug: "surat", name: "Surat", country: "india", aliasSlug: "silver-rate-surat" },
  { slug: "kanpur", name: "Kanpur", country: "india", aliasSlug: "silver-rate-kanpur" },
  { slug: "nagpur", name: "Nagpur", country: "india", aliasSlug: "silver-rate-nagpur" },
];

export function silverCurrencyFor(country: SilverCountry): "PKR" | "INR" {
  return country === "pakistan" ? "PKR" : "INR";
}

export type SilverPrices = {
  perTola: number;
  perGram: number;
  per10g: number;
  perKg: number;
};

/**
 * Live silver formula (per spec):
 *   perTola(local) = (spotUsdPerOz / 2.67) * fxRate + 400
 * Derived units come from per-gram via standard conversions.
 */
export function computeSilverPrices(args: {
  spotUsdPerOz: number;
  fxRate: number;
  country: SilverCountry;
}): SilverPrices {
  // Defensive coercion — any missing/NaN input collapses to 0 so downstream
  // formatters never receive NaN (Googlebot headless crash guard).
  const safeSpot = Number.isFinite(args.spotUsdPerOz) ? args.spotUsdPerOz : 0;
  const safeFx = Number.isFinite(args.fxRate) ? args.fxRate : 0;
  let perTola: number;
  let perGram: number;
  if (args.country === "pakistan") {
    perTola = (safeSpot / OZ_PER_TOLA_FORMULA) * safeFx + SILVER_PREMIUM_PK;
    perGram = perTola / GRAMS_PER_TOLA;
  } else {
    const per10g =
      ((safeSpot / GRAMS_PER_OZ) * safeFx * 10 + SILVER_PREMIUM_IN) *
      INDIA_SILVER_DUTY_MULTIPLIER;
    perGram = per10g / 10;
    perTola = perGram * GRAMS_PER_TOLA;
  }
  if (safeSpot === 0 || safeFx === 0 || !Number.isFinite(perTola)) {
    perTola = 0;
    perGram = 0;
  }
  return {
    perTola,
    perGram,
    per10g: perGram * 10,
    perKg: perGram * 1000,
  };
}

export function formatSilverMoney(value: number, currency: "PKR" | "INR"): string {
  const symbol = currency === "PKR" ? "Rs " : "₹";
  const safe = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return symbol + Math.round(safe).toLocaleString(currency === "PKR" ? "en-PK" : "en-IN");
}